import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Instagram, Menu, X } from "lucide-react";
import { INSTAGRAM_URL } from "../../data/content";
import { scrollToSection } from "../../lib/scroll";

const links = [
    { hash: "#historia", label: "Historia", testId: "nav-biography-link" },
    { hash: "#libros", label: "Libros", testId: "nav-books-link" },
    { hash: "#poemas", label: "Poemas", testId: "nav-poems-link" },
    { hash: "#video-poesia", label: "Poesía In And Out", testId: "nav-videopoetry-link" },
    { hash: "#canciones", label: "Canciones", testId: "nav-songs-link" },
    { hash: "#contacto", label: "Contacto", testId: "nav-contact-link" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", fn, { passive: true });
        fn();
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const go = (hash) => {
        setOpen(false);
        scrollToSection(hash);
    };

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-[70] transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "bg-paper/85 backdrop-blur-md border-b border-line"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                    <button
                        data-testid="nav-brand-link"
                        onClick={() => go("#inicio")}
                        className="text-left shrink-0"
                    >
                        <span className="block font-display text-[16px] sm:text-2xl tracking-tight leading-tight text-ink">
                            José Luis <span className="italic text-crimson">Garcés Maldonado</span>
                        </span>
                        <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.28em] leading-tight text-subtle">
                            Quito, Ecuador
                        </span>
                    </button>

                    <div className="hidden xl:flex items-center gap-5">
                        {links.map((l) => (
                            <button
                                key={l.hash}
                                data-testid={l.testId}
                                onClick={() => go(l.hash)}
                                className="link-underline whitespace-nowrap shrink-0 font-mono text-[10px] uppercase tracking-[0.18em] text-subtle hover:text-ink transition-colors"
                            >
                                {l.label}
                            </button>
                        ))}
                        <a
                            data-testid="nav-merch-link"
                            href="/versos-para-llevar"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-underline whitespace-nowrap shrink-0 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-crimson hover:text-ink transition-colors"
                        >
                            Versos para llevar
                            <ArrowUpRight size={12} />
                        </a>
                        <a
                            data-testid="nav-instagram-cta"
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whitespace-nowrap shrink-0 flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink hover:bg-ink hover:text-paper transition-colors"
                        >
                            <Instagram size={14} />
                            Instagram
                        </a>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-4 xl:hidden">
                        <a
                            data-testid="nav-merch-link-mobile"
                            href="/versos-para-llevar"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 whitespace-nowrap py-2 font-display italic text-[14px] leading-none text-crimson hover:text-ink transition-colors"
                        >
                            Versos para llevar
                            <ArrowUpRight size={12} />
                        </a>
                        <button
                            data-testid="nav-mobile-menu-button"
                            onClick={() => setOpen(true)}
                            className="p-2 text-ink"
                            aria-label="Abrir menú"
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </nav>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[85] bg-ink text-paper flex flex-col"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <div className="h-[72px] px-5 flex items-center justify-between">
                            <span className="font-display text-xl">José Luis Garcés Maldonado</span>
                            <button
                                data-testid="nav-mobile-close-button"
                                onClick={() => setOpen(false)}
                                className="p-2"
                                aria-label="Cerrar menú"
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <div className="flex-1 flex flex-col justify-center px-8 gap-2">
                            {links.map((l, i) => (
                                <motion.button
                                    key={l.hash}
                                    data-testid={`nav-mobile-${l.testId}`}
                                    onClick={() => go(l.hash)}
                                    className="text-left font-display text-4xl sm:text-5xl font-light py-2 hover:italic hover:text-gold transition-colors"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5 }}
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.a
                                data-testid="nav-mobile-merch-link"
                                href="/versos-para-llevar"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-left font-display text-4xl sm:text-5xl font-light py-2 text-crimson hover:italic transition-colors flex items-center gap-3"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.08 + links.length * 0.07, duration: 0.5 }}
                            >
                                Versos para llevar
                                <ArrowUpRight size={26} />
                            </motion.a>
                            <motion.a
                                data-testid="nav-mobile-instagram-cta"
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <Instagram size={16} /> @joseluisgarcespoesia
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
